import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "./firebase";
import type { DietType } from "./userStats";

export interface DietaryPreferences {
  dietType: DietType;
  allergies: string[];
}

function getDietaryContext(prefs?: DietaryPreferences): string {
  if (!prefs) return "";

  const parts: string[] = [];

  if (prefs.dietType && prefs.dietType !== "none") {
    const dietLabels: Record<string, string> = {
      vegetarian: "vegetarian (no meat or fish)",
      vegan: "vegan (no animal products)",
      pescatarian: "pescatarian (fish but no meat)",
      keto: "keto (low-carb, high-fat)",
      paleo: "paleo (whole foods, no grains/dairy)",
    };
    parts.push(`The user follows a ${dietLabels[prefs.dietType] || prefs.dietType} diet.`);
  }

  if (prefs.allergies && prefs.allergies.length > 0) {
    const allergyLabels: Record<string, string> = {
      gluten: "gluten",
      dairy: "dairy",
      nuts: "tree nuts",
      peanuts: "peanuts",
      shellfish: "shellfish",
      eggs: "eggs",
      soy: "soy",
      fish: "fish",
    };
    const allergyList = prefs.allergies.map((a) => allergyLabels[a] || a).join(", ");
    parts.push(`The user has allergies/intolerances to: ${allergyList}. NEVER suggest foods containing these.`);
  }

  if (parts.length === 0) return "";

  return `\n\n## User's Dietary Requirements (MUST RESPECT)\n${parts.join("\n")}\n`;
}

// Initialize Firebase Functions
const functions = getFunctions(app);
const geminiProxy = httpsCallable<GeminiRequest, GeminiResponse>(functions, "geminiProxy");

// Request/response types
interface MealPlanConstraints {
  prepTimeMinutes: number;
  targetGI: string;
  servings: number;
  excludeIngredients: string;
}

interface GeminiRequest {
  action: "chat" | "analyzeRecipe" | "analyzeMeal" | "analyzeMenu" | "optimizeGrocery" | "quickOrder" | "generateMealPlan" | "regenerateMeal";
  history?: ChatMessage[];
  message?: string;
  imageData?: { mimeType: string; data: string };
  dietaryContext?: string;
  foods?: string[];
  recipe?: string;
  groceryList?: string;
  restaurant?: string;
  budget?: string;
  // Meal planner fields
  constraints?: MealPlanConstraints;
  mealType?: string;
  currentMealName?: string;
}

interface GeminiResponse {
  response: string;
}

export interface ChatMessage {
  role: "user" | "model";
  content: string;
  image?: string;
}

export async function sendMessage(
  history: ChatMessage[],
  message: string,
  imageData?: { mimeType: string; data: string },
  dietaryPrefs?: DietaryPreferences
): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "chat",
    history: history.map((msg) => ({ role: msg.role, content: msg.content })),
    message,
    imageData,
    dietaryContext,
  });

  return result.data.response;
}

export async function analyzeRecipe(recipe: string, dietaryPrefs?: DietaryPreferences): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "analyzeRecipe",
    recipe,
    dietaryContext,
  });

  return result.data.response;
}

export async function analyzeMeal(foods: string[], dietaryPrefs?: DietaryPreferences): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "analyzeMeal",
    foods,
    dietaryContext,
  });

  return result.data.response;
}

export async function analyzeMenu(
  imageData: { mimeType: string; data: string },
  dietaryPrefs?: DietaryPreferences
): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "analyzeMenu",
    imageData,
    dietaryContext,
  });

  return result.data.response;
}

export async function optimizeGroceryList(list: string, dietaryPrefs?: DietaryPreferences): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "optimizeGrocery",
    groceryList: list,
    dietaryContext,
  });

  return result.data.response;
}

export async function buildQuickOrder(
  restaurant: string,
  request: string,
  budget?: string,
  dietaryPrefs?: DietaryPreferences
): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "quickOrder",
    restaurant,
    message: request,
    budget,
    dietaryContext,
  });

  return result.data.response;
}

export async function generateMealPlan(
  constraints: MealPlanConstraints,
  dietaryPrefs?: DietaryPreferences
): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "generateMealPlan",
    constraints,
    dietaryContext,
  });

  return result.data.response;
}

export async function regenerateMeal(
  mealType: string,
  currentMealName: string,
  constraints: MealPlanConstraints,
  dietaryPrefs?: DietaryPreferences
): Promise<string> {
  const dietaryContext = getDietaryContext(dietaryPrefs);

  const result = await geminiProxy({
    action: "regenerateMeal",
    mealType,
    currentMealName,
    constraints,
    dietaryContext,
  });

  return result.data.response;
}
